'use client';

import { ChevronDown, ChevronUp } from '@ps/web-fusion-icons';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import classNames from 'clsx';
import type { KeyboardEvent, ReactNode, RefObject } from 'react';
import { createRef, useEffect, useRef, useState } from 'react';
import { Button, Disclosure, DisclosurePanel, Label } from 'react-aria-components';
import type { DisclosureProps } from 'react-aria-components';

import Checkbox from '../Checkbox/Checkbox';
import Flex from '../Flex/Flex';
import Icon from '../Icon/Icon';
import { useFusionTheme } from '../hooks';

import styles from './Filter.module.scss';

const headingContainerVariants = cva(styles.headingContainer, {
  variants: {
    size: {
      default: '',
      small: styles.headingContainerSmall
    }
  }
});

const checkboxContainerVariants = cva(styles.checkboxContainer, {
  variants: {
    size: {
      default: '',
      small: styles.checkboxContainerSmall
    }
  }
});

export type FilterProps = Exclude<DisclosureProps, 'style'> & {
  label?: string;
  headingText: string;
  children: ReactNode;
  selectionItems: string[];
  defaultSelectedItemsIndexes?: number[];
  selectedItemsIndexes?: number[];
  onSelectionChange?: (selectedItems: string[]) => void;
} & VariantProps<typeof headingContainerVariants>;

export function Filter({
  label,
  size,
  headingText,
  children,
  selectionItems,
  defaultSelectedItemsIndexes = [],
  selectedItemsIndexes,
  onSelectionChange,
  className,
  ...props
}: FilterProps) {
  const { addThemeClassName } = useFusionTheme();

  const [uncontrolledSelectedItems, setUncontrolledSelectedItems] = useState<string[]>(() =>
    defaultSelectedItemsIndexes
      .filter((index) => index >= 0 && index < selectionItems.length)
      .map((index) => selectionItems[index])
  );

  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const checkboxRefs = useRef<RefObject<HTMLInputElement>[]>([]);

  const isControlled = selectedItemsIndexes !== undefined;
  const selectedItems: string[] = isControlled
    ? selectedItemsIndexes
        .filter((index) => index >= 0 && index < selectionItems.length)
        .map((index) => selectionItems[index])
    : uncontrolledSelectedItems;

  useEffect(() => {
    checkboxRefs.current = selectionItems.map(
      (_, i) => checkboxRefs.current[i] ?? createRef<HTMLInputElement>()
    );
  }, [selectionItems]);

  const handleSelectionChange = (key: string, isSelected: boolean) => {
    const updateSelection = (prev: string[]): string[] =>
      isSelected ? [...prev, key] : prev.filter((item) => item !== key);

    if (!isControlled) {
      const newSelection = updateSelection(uncontrolledSelectedItems);
      setUncontrolledSelectedItems(newSelection);
      onSelectionChange?.(newSelection);
    } else {
      const newSelection = updateSelection(selectedItems);
      onSelectionChange?.(newSelection);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const direction = e.key === 'ArrowDown' ? 1 : -1;
      const newIndex = Math.max(0, Math.min(focusedIndex + direction, selectionItems.length - 1));
      setFocusedIndex(newIndex);
      checkboxRefs.current[newIndex]?.current?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (focusedIndex >= 0 && focusedIndex < selectionItems.length) {
        const item = selectionItems[focusedIndex];
        const isSelected = selectedItems.includes(item);
        handleSelectionChange(item, !isSelected);
      }
    }
  };

  const handleFocus = (index: number) => {
    setFocusedIndex(index);
  };

  const shouldShowCountBadge = selectedItems.length > 0;

  return (
    <Flex direction="column" gap="2xs" className={addThemeClassName(className)}>
      {label && <Label className={addThemeClassName(styles.label)}>{label}</Label>}
      <Disclosure className={addThemeClassName(styles.disclosure, className)} {...props}>
        {({ isExpanded, isDisabled }) => (
          <>
            <Button className={headingContainerVariants({ size })} slot="trigger">
              <div className={styles.contentWrapper}>
                <span
                  className={classNames(styles.textContent, {
                    [styles.filled]: shouldShowCountBadge && !isDisabled
                  })}
                >
                  {headingText}
                </span>
                {shouldShowCountBadge && (
                  <span className={styles.countBadge}>({selectedItems.length})</span>
                )}
              </div>
              <span className={styles.iconWrapper}>
                <Icon icon={isExpanded && !isDisabled ? ChevronUp : ChevronDown} />
              </span>
            </Button>
            <DisclosurePanel className={styles.panel}>
              <div className={checkboxContainerVariants({ size })} onKeyDown={handleKeyDown}>
                {selectionItems.map((selection, index) => (
                  <Checkbox
                    key={selection}
                    value={selection}
                    isSelected={selectedItems.includes(selection)}
                    onChange={(isSelected) => handleSelectionChange(selection, isSelected)}
                    inputRef={checkboxRefs.current[index]}
                    onFocusChange={() => handleFocus(index)}
                  >
                    {selection}
                  </Checkbox>
                ))}
              </div>
            </DisclosurePanel>
          </>
        )}
      </Disclosure>
    </Flex>
  );
}

export default Filter;
